import { useApolloClient } from '@apollo/client'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Paper, Skeleton, Stack, Tab } from '@mui/material'
import {
    RosterProfileQueryVariables,
    type RosterProfileQuery,
} from '__generated__/graphql'
import { type FC, useState } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import {
    preMbaColumns,
    postMbaColumns,
    portOpsColumns,
    allColumns,
} from './columns'
import { RosterSearchProvider } from './columns/RosterSearchContext'
import { useRosterSearchColumns } from './columns/useRosterSearchColumns'
import {
    ColumnTemplateDropdown,
    type ColumnTemplateId,
} from './ColumnTemplateDropdown'
import {
    CandidateSqlSearchProvider,
    GetCandidatesForRosterSql,
} from 'recruiter/contexts'
import { CandidateSearchSql } from 'recruiter/pages/Research/CandidateSearchSql'
import { recruiterRouteConfig } from 'recruiter/routes'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getPxAsRem } from 'shared/utils'
import { CandidateRosterNoteDrawer } from '../CandidateRosterNoteDrawer'
import CompareTable from '../CompareTable/CompareTable'
import HistoricalBlasts from './HistoricalBlasts'
import { RosterCandidateContextTabs } from './RosterCandidateContextTabs'
import { ReferenceSearchesSelector } from './ReferenceSearchesSelector'
import { RosterSearchBulkActions } from './RosterSearchBulkActions'
import RosterSearchHeader from './RosterSearchHeader'
import { ToolbarColorFilter } from './ToolbarColorFilter'
import { ToolbarScratchPad } from './ToolbarScratchPad'

type RosterTabId = 'candidates' | 'comparison' | 'blasts' | 'status-summary'
export type RosterCandidateTableViewId = 'all' | 'on-roster'
type RosterCandidateTableViewIdNew = 'all' | 'on'

const deriveActiveTab = (pathname: string): RosterTabId => {
    if (pathname.includes('/comparison')) return 'comparison'
    if (pathname.includes('/blasts')) return 'blasts'
    if (pathname.includes('/status-summary')) return 'status-summary'
    return 'candidates'
}

const deriveCandidateViewNew = (
    pathname: string
): RosterCandidateTableViewIdNew => {
    if (pathname.endsWith('/all')) return 'all'
    return 'on'
}

export interface RosterSearchProfileProps {
    roster: RosterProfileQuery['roster']
    refetch: ApolloQueryRefetchFunction<
        RosterProfileQuery,
        RosterProfileQueryVariables
    >
    loading?: boolean
}

const getTemplateColumns = (template: ColumnTemplateId) => {
    switch (template) {
        case 'pre-mba':
            return preMbaColumns
        case 'post-mba':
            return postMbaColumns
        case 'port-ops':
            return portOpsColumns
        case 'all':
            return allColumns
    }
}

const RosterSearchCandidatesContent: FC<{
    selectedTemplate: ColumnTemplateId
    onSelectTemplate: (t: ColumnTemplateId) => void
}> = ({ selectedTemplate, onSelectTemplate }) => {
    const templateColumns = getTemplateColumns(selectedTemplate)
    const { columns, columnGroupingModel } =
        useRosterSearchColumns(templateColumns)

    return (
        <CandidateSearchSql
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            afterSearchContent={
                <Stack direction="row" alignItems="center" gap={1}>
                    <ToolbarColorFilter />
                    <ToolbarScratchPad />
                </Stack>
            }
            topActionButtons={<RosterSearchBulkActions />}
            endContent={
                <Stack direction="row" alignItems="center" gap={1}>
                    <ReferenceSearchesSelector />
                    <RosterCandidateContextTabs />
                </Stack>
            }
            footerStartContent={
                <ColumnTemplateDropdown
                    selectedTemplate={selectedTemplate}
                    onSelectTemplate={onSelectTemplate}
                />
            }
            pinnedColumns={{
                left: [
                    '__check__',
                    'candidate',
                    'status',
                    'colors',
                    'rosterCandidateFavoriteCount',
                    'processStatus',
                ],
            }}
        />
    )
}

const RosterSearchProfile = ({
    roster,
    refetch,
    loading = false,
}: RosterSearchProfileProps) => {
    const apolloClient = useApolloClient()
    const location = useLocation()
    const { rosterId } = useParams<{ rosterId: string }>()

    const activeTab = deriveActiveTab(location.pathname)
    const tabLabels: Record<RosterTabId, string> = {
        candidates: 'Candidates',
        comparison: 'Compare',
        blasts: 'Blasts',
        'status-summary': 'Status',
    }
    usePageTitle(
        roster?.rosterName
            ? `${roster.rosterName} · ${tabLabels[activeTab]}`
            : undefined
    )
    const activeCandidateTableViewNew = deriveCandidateViewNew(
        location.pathname
    )

    const basePath = `${recruiterRouteConfig.rosters.path}/${rosterId}`

    const [selectedTemplate, setSelectedTemplate] =
        useState<ColumnTemplateId>('pre-mba')

    const [actionableRosterCandidateId, setActionableRosterCandidateId] =
        useState('')
    const [isRosterNoteDrawerOpen, setIsRosterNoteDrawerOpen] = useState(false)

    const handleNoteSaved = () => {
        apolloClient.refetchQueries({
            include: [GetCandidatesForRosterSql],
        })
    }

    const handleOpenNoteDrawer = (rosterCandidateId: string) => {
        setActionableRosterCandidateId(rosterCandidateId)
        setIsRosterNoteDrawerOpen(true)
    }

    if (!roster) {
        return loading ? (
            <Stack
                sx={{
                    padding: getPxAsRem(16, 24),
                    gap: getPxAsRem(16),
                }}
            >
                <Skeleton variant="rounded" height="40vh" />
                <Skeleton variant="rounded" height="50vh" />
            </Stack>
        ) : (
            <Navigate to={recruiterRouteConfig.rosters.path} />
        )
    }

    const verticalSpacingInPixels = 16

    return (
        <Stack
            gap={getPxAsRem(verticalSpacingInPixels)}
            padding={getPxAsRem(verticalSpacingInPixels, 24)}
        >
            <RosterSearchHeader data={roster} refetch={refetch} />

            <Stack
                component={Paper}
                borderRadius="8px"
                height="90vh"
                overflow="hidden"
            >
                <TabContext value={activeTab}>
                    <TabList variant="fullWidth">
                        <Tab
                            label="Candidates"
                            value="candidates"
                            component={Link}
                            to={`${basePath}/candidates/${activeCandidateTableViewNew}`}
                        />
                        <Tab
                            label="Compare"
                            value="comparison"
                            component={Link}
                            to={`${basePath}/comparison`}
                        />
                        <Tab
                            label="Blast History"
                            value="blasts"
                            component={Link}
                            to={`${basePath}/blasts`}
                        />
                    </TabList>

                    <TabPanel
                        value="candidates"
                        sx={{
                            padding: 0,
                            overflow: 'hidden',
                            height: '100%',
                        }}
                    >
                        <RosterSearchProvider
                            rosterId={roster.id}
                            canEdit={roster.permissions?.canEdit ?? false}
                            viewMode={activeCandidateTableViewNew}
                            onOpenNoteDrawer={handleOpenNoteDrawer}
                        >
                            <CandidateSqlSearchProvider
                                key={`searches-candidates-${activeCandidateTableViewNew}-${selectedTemplate}`}
                                rosterId={
                                    activeCandidateTableViewNew === 'on'
                                        ? roster.id
                                        : undefined
                                }
                                enrichmentRosterId={
                                    activeCandidateTableViewNew === 'all'
                                        ? roster.id
                                        : undefined
                                }
                            >
                                <RosterSearchCandidatesContent
                                    selectedTemplate={selectedTemplate}
                                    onSelectTemplate={setSelectedTemplate}
                                />
                            </CandidateSqlSearchProvider>
                        </RosterSearchProvider>
                    </TabPanel>

                    <TabPanel value="comparison" sx={{ overflow: 'auto' }}>
                        <CompareTable
                            roster={roster}
                            refetchHeaderData={refetch}
                        />
                    </TabPanel>

                    <TabPanel value="blasts" sx={{ overflow: 'auto' }}>
                        <HistoricalBlasts rosterId={roster.id || ''} />
                    </TabPanel>
                </TabContext>
            </Stack>

            <CandidateRosterNoteDrawer
                canEdit={roster.permissions?.canEdit ?? false}
                isOpen={isRosterNoteDrawerOpen}
                onClose={() => setIsRosterNoteDrawerOpen(false)}
                onSave={handleNoteSaved}
                rosterCandidateId={actionableRosterCandidateId}
            />
        </Stack>
    )
}

export default RosterSearchProfile
